// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import {
  BOX_FOLDERS_ORDER_DEFAULT,
  COMPANY_PERSONAL_STRUCTURE_TYPES,
  ONBOARDING_QUERY_TYPES,
  ROUTES,
  USER_ROLES,
  VERIFIED_VERIFICATION_STATES,
} from '@/constants'
import { SECTIONS } from '@/types/sections'
import isEmpty from 'lodash/isEmpty'
import { ANALYZE_STATUS } from '@deepcloud/api-deep-box'
import {
  convertOrderString,
  MIME_TYPES,
  sortStringToObject,
} from '@deepcloud/deep-ui-utils'
import type { Sections } from '@/types/sections.ts'
import type { NodeContentParams } from '@deepcloud/api-deep-box'
import type { RouteLocation, RouteLocationNamedRaw } from 'vue-router'

export default {
  getDefaultOrderFetchParamsBySection(section: Sections) {
    let orderString = ''

    switch (section) {
      case 'inbox':
      case 'queue':
        orderString = BOX_FOLDERS_ORDER_DEFAULT.INBOX
        break
      case 'files':
        orderString = BOX_FOLDERS_ORDER_DEFAULT.FILES
        break

      case 'trash':
        orderString = BOX_FOLDERS_ORDER_DEFAULT.TRASH

      // no default
    }

    return this.getFetchParameters(sortStringToObject(orderString))
  },
  getSection(path) {
    if (isEmpty(path)) {
      return undefined
    }
    const sectionSegment = path.segments[0]
    const isSectionFolder = path.segments.length > 1

    switch (sectionSegment?.mimeType) {
      case MIME_TYPES.MIME_TYPE_FILES:
        if (isSectionFolder) {
          return SECTIONS.FILE_NODES as Sections
        }
        return SECTIONS.FILES as Sections

      case MIME_TYPES.MIME_TYPE_TRASH:
        if (isSectionFolder) {
          return SECTIONS.TRASH_NODES as Sections
        }
        return SECTIONS.TRASH as Sections

      case MIME_TYPES.MIME_TYPE_INBOX:
        return SECTIONS.QUEUE as Sections
      default:
        return undefined
    }
  },
  getCurrentSection(routeName: string | null) {
    switch (routeName) {
      case ROUTES.ROUTE_FILES_NODE:
        return SECTIONS.FILE_NODES
      case ROUTES.ROUTE_FILES:
        return SECTIONS.FILES
      case ROUTES.ROUTE_TRASH_NODE:
        return SECTIONS.TRASH_NODES
      case ROUTES.ROUTE_TRASH:
        return SECTIONS.TRASH
      case ROUTES.ROUTE_QUEUE:
        return SECTIONS.QUEUE
      default:
        return undefined
    }
  },

  getSectionFromRoute(route: RouteLocationNamedRaw | RouteLocation) {
    switch (route.name) {
      case ROUTES.ROUTE_QUEUE:
        return SECTIONS.QUEUE as Sections
      case ROUTES.ROUTE_FILES:
        return SECTIONS.FILES as Sections
      case ROUTES.ROUTE_FILES_NODE:
        return SECTIONS.FILE_NODES as Sections
      case ROUTES.ROUTE_TRASH:
        return SECTIONS.TRASH as Sections
      case ROUTES.ROUTE_TRASH_NODE:
        return SECTIONS.TRASH_NODES as Sections
      default:
        return null
    }
  },
  getUploadSectionFromMimeType(mime: string): Sections {
    switch (mime) {
      case MIME_TYPES.MIME_TYPE_INBOX:
        return 'queue'
      case MIME_TYPES.MIME_TYPE_FILES:
        return 'files'
      case MIME_TYPES.MIME_TYPE_TRASH:
        return 'trash'
      default:
        // there is no possible to upload files to trash
        return 'fileNodes'
    }
  },
  getRouteNameBySectionName(section: Sections) {
    switch (section) {
      case 'inbox':
      case 'queue':
        return 'organization-types-type-boxes-box-inbox'
      case 'files':
        return 'organization-types-type-boxes-box-files'
      case 'fileNodes':
        return 'organization-types-type-boxes-box-files-node'
      case 'trash':
        return 'organization-types-type-boxes-box-trash'
      case 'trashNodes':
        return 'organization-types-type-boxes-box-trash-node'
      default:
        return undefined
    }
  },
  boxSortAlphaNum<T>(boxes: T[]) {
    return boxes.sort((a, b) => {
      const boxAName = a.boxName || a.name
      const boxBName = b.boxName || b.name
      return boxAName.localeCompare(boxBName, undefined, { numeric: true })
    })
  },
  /**
   * Get sorter from params.order attribute of a node state
   * @param {string} order
   * @returns {string}
   */
  getSorter(order) {
    const sortBy = order.sortBy[0]
    // select the right sorting function
    let sorter
    switch (sortBy) {
      case 'size': // if sorting property is a number
        sorter = (item) => item[sortBy]
        break // if sorting property is a date
      case 'modified.time':
        sorter = (item) => new Date(item.modified.time)
        break
      default:
        // default: lowercase string
        sorter = (item) => item[sortBy].toLowerCase()
    }
    return sorter
  },
  getFetchParameters(payload): NodeContentParams {
    // create the object with default values

    const output = {
      limit: payload.limit || 50,
    }
    // list of accepted properties, to override the default object
    const accepted = ['limit', 'offset']

    accepted.forEach((k) => {
      if (payload[k]) {
        output[k] = payload[k]
      }
    })

    // if sortBy property is set, create a corresponding 'order' query string
    if (payload.sortBy && payload.sortBy.length > 0) {
      const sort = []
      if (payload.sortBy[0].key) {
        sort[0] = convertOrderString(payload.sortBy[0].key, true)
      } else {
        sort[0] = convertOrderString(payload.sortBy[0], true)
      }
      if (payload.sortBy[0].order) {
        sort[1] = payload.sortBy[0].order
      } else {
        sort[1] = payload.sortDesc[0] === true ? 'desc' : 'asc'
      }
      output.order = `${sort.join(' ')}`
    }

    return output
  },
  getReadableName(node, i18n) {
    if (!i18n) {
      console.log('[i18n] instance was not provided ')
      return ''
    }
    if (node) {
      switch (node?.mimeType) {
        case MIME_TYPES.MIME_TYPE_INBOX:
          return i18n.t('navigation.tabs.inbox')
        case MIME_TYPES.MIME_TYPE_FILES:
          return i18n.t('navigation.tabs.files')
        case MIME_TYPES.MIME_TYPE_TRASH:
          return i18n.t('navigation.tabs.trash')
        default:
          return node?.displayName || node?.name
      }
    }
    return undefined
  },
  showVerificationAlert(selectedOrganization, organizations) {
    if (!selectedOrganization) return undefined
    const currentOrganization = this.currentOrganization(
      selectedOrganization,
      organizations,
    )
    if (isEmpty(currentOrganization)) {
      return false
    }
    const isOrgOnboardedDeepV =
      currentOrganization?.metadata?.onboarded_with ===
      ONBOARDING_QUERY_TYPES.ONBOARDING_QUERY_TYPE_DEEPV
    const isOrgOnboardedDeepSign =
      currentOrganization?.metadata?.onboarded_with ===
      ONBOARDING_QUERY_TYPES.ONBOARDING_QUERY_TYPE_DEEPSIGN
    return (
      currentOrganization.roles.includes(USER_ROLES.USER_ROLE_MANAGE_COMPANY) &&
      !VERIFIED_VERIFICATION_STATES.includes(
        currentOrganization.verification_state,
      ) &&
      !isOrgOnboardedDeepV &&
      !isOrgOnboardedDeepSign
    )
  },
  isPersonalStructure(organization) {
    if (!organization) return undefined
    return COMPANY_PERSONAL_STRUCTURE_TYPES.includes(organization.structure)
  },
  isOwnedOrganization(organization) {
    if (!organization) return undefined
    const ownerRoles = [
      USER_ROLES.USER_ROLE_MANAGE_COMPANY,
      USER_ROLES.USER_ROLE_MANAGE_MEMBERS,
      USER_ROLES.USER_ROLE_MANAGE_BOXES,
      USER_ROLES.USER_ROLE_VIEW_COMPANY,
      USER_ROLES.USER_ROLE_ORDER_SUB,
    ]
    const organizationRoles = organization?.roles || []
    return ownerRoles.every((role) => organizationRoles.includes(role))
  },
  isPersonalSpace(organization) {
    if (!organization) return undefined
    return (
      this.isOwnedOrganization(organization) &&
      this.isPersonalStructure(organization)
    )
  },
  currentOrganization(selectedOrganization, organizations) {
    if (!selectedOrganization) return undefined
    return organizations.find(
      (organization) => organization.group_id === selectedOrganization.group_id,
    )
  },
  getDeepEditorUrl({
    analysisStatus,
    nodeId,
    deepBoxNodeId,
    groupId,
    boxNodeId,
  }) {
    if (analysisStatus === ANALYZE_STATUS.ANALYSIS_STATUS_RUNNING) {
      return '#'
    }
    const hostOrigin = window.location.origin
    const editorUrl = `${
      import.meta.env.VITE_DEEPBOX_EDITOR_BASE_URL
    }/${groupId}/extract/${deepBoxNodeId}/${boxNodeId}/${nodeId}`
    const backLink = `?backLink=${hostOrigin}/types/${deepBoxNodeId}/boxes/${boxNodeId}/inbox`
    return editorUrl + backLink
  },
  hasUserRole(userRole, organization) {
    const organizationRoles = organization?.roles || []
    return organizationRoles.includes(userRole)
  },
  /**
   * Sort nodes in order to have folders always first
   * @param nodes
   * @return [Object]
   */
  getFoldersFirst(nodes) {
    const folders = []
    const files = []

    nodes.forEach((node) => {
      if (node.type === 'folder') {
        folders.push(node)
      } else {
        files.push(node)
      }
    })

    return folders.concat(files)
  },
}
